import { useEffect, useRef, useState, useCallback } from "react";
import { io } from "socket.io-client";
import TopNavBar from "./TopNavBar";
import { useLocation, useNavigate } from "react-router-dom";

const socket = io("https://socketserver-ftq3.onrender.com");

function safeTrim(value) {
  return String(value ?? "").trim();
}

export default function Chat() {
  const location = useLocation();
  const navigate = useNavigate();

  const loggedName = safeTrim(sessionStorage.getItem("loggedUserName"));
  const [username, setUsername] = useState(loggedName);
  const [nameInput, setNameInput] = useState("");

  const [messages, setMessages] = useState({}); // Store messages per conversation
  const [messageInput, setMessageInput] = useState("");
  const [typingUsers, setTypingUsers] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [showEmojis, setShowEmojis] = useState(false);
  const [sendingFile, setSendingFile] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [selectedUserForDM, setSelectedUserForDM] = useState(null);
  const [conversationRequests, setConversationRequests] = useState([]);
  const [activeConversations, setActiveConversations] = useState([]); // Track active conversations
  const [unreadMessages, setUnreadMessages] = useState({}); // Track unread messages per conversation
  const [showSidebar, setShowSidebar] = useState(false); // For mobile sidebar toggle

  const dmUser = location?.state?.toUser || selectedUserForDM || null;
  const dmUsername = safeTrim(dmUser?.username);
  const isDM = Boolean(dmUsername);

  const listRef = useRef(null);
  const pendingMessagesRef = useRef(new Map());

  const getMessageKey = useCallback((msg) => {
    const clientId = safeTrim(msg?.clientMessageId);
    if (clientId) return `cid:${clientId}`;

    const t = msg?.timestamp ? new Date(msg.timestamp).getTime() : 0;
    const from = safeTrim(msg?.username);
    const to = safeTrim(msg?.to);
    const type = safeTrim(msg?.type) || "text";
    const text = safeTrim(msg?.text);
    const fileName = safeTrim(msg?.file?.name);
    return `f:${from}|to:${to}|ty:${type}|t:${t}|tx:${text}|fn:${fileName}`;
  }, []);

  // Get conversation key
  const getConversationKey = useCallback((user1, user2) => {
    return [user1, user2].sort().join('_');
  }, []);

  // Add notification
  const addNotification = useCallback((notification) => {
    const id = Date.now();
    setNotifications(prev => [{ id, ...notification }, ...prev].slice(0, 50));
    setUnreadCount(prev => prev + 1);
    
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 5000);
  }, []);

  const pushMessage = useCallback((msg) => {
    const clientMessageId = safeTrim(msg?.clientMessageId);
    
    if (clientMessageId && pendingMessagesRef.current.has(clientMessageId)) {
      pendingMessagesRef.current.delete(clientMessageId);
      return;
    }

    // Determine which conversation this message belongs to
    const from = safeTrim(msg?.username);
    const to = safeTrim(msg?.to);
    let conversationKey = null;
    
    if (to && to !== username) {
      // This is a DM message
      const otherUser = from === username ? to : from;
      conversationKey = getConversationKey(username, otherUser);
    } else if (to && to === username) {
      // DM message sent to current user
      conversationKey = getConversationKey(username, from);
    } else if (!to && from !== username) {
      // Global chat message (not from current user)
      conversationKey = "global";
    } else if (!to && from === username) {
      // Global chat message from current user
      conversationKey = "global";
    }

    if (!conversationKey) return;

    const key = getMessageKey(msg);
    if (!key) return;
    
    setMessages((prev) => {
      const conversationMessages = prev[conversationKey] || [];
      const exists = conversationMessages.some(m => {
        const existingKey = getMessageKey(m);
        return existingKey === key;
      });
      
      if (exists) return prev;
      
      // Mark as unread if message is from someone else and not in current conversation
      if (msg.username !== username && 
          (conversationKey !== (isDM ? getConversationKey(username, dmUsername) : "global"))) {
        setUnreadMessages(prevUnread => ({
          ...prevUnread,
          [conversationKey]: (prevUnread[conversationKey] || 0) + 1
        }));
      }
      
      return {
        ...prev,
        [conversationKey]: [...conversationMessages, msg]
      };
    });
  }, [username, isDM, dmUsername, getConversationKey, getMessageKey]);

  const getCurrentMessages = useCallback(() => {
    if (!isDM) {
      return messages["global"] || [];
    } else {
      const conversationKey = getConversationKey(username, dmUsername);
      return messages[conversationKey] || [];
    }
  }, [isDM, messages, username, dmUsername, getConversationKey]);


  useEffect(() => {
    const onMessage = (message) => {
      pushMessage(message);
      
      // Add notification for new message if not from current user
      if (message.username !== username) {
        const to = safeTrim(message?.to);
        const isDMToCurrentUser = to === username;
        
        if (!to || isDMToCurrentUser) {
          addNotification({
            type: "message",
            message: `${message.username}: ${message.text || "sent a message"}`,
            from: message.username,
            timestamp: new Date(),
          });
        }
      }
    };

    const onTypingStart = ({ username: otherUsername, senderId, to } = {}) => {
      if (!otherUsername) return;
      if (senderId && senderId === socket.id) return;
      if (isDM && to && to !== username) return;
      if (isDM && to !== dmUsername && otherUsername !== dmUsername) return;
      
      setTypingUsers((prev) =>
        prev.includes(otherUsername) ? prev : [...prev, otherUsername]
      );
    };

    const onTypingStop = ({ username: otherUsername, senderId, to } = {}) => {
      if (!otherUsername) return;
      if (senderId && senderId === socket.id) return;
      if (isDM && to && to !== username) return;
      if (isDM && to !== dmUsername && otherUsername !== dmUsername) return;
      
      setTypingUsers((prev) => prev.filter((u) => u !== otherUsername));
    };

    const onOnlineUsers = (users) => {
      setOnlineUsers(users.filter(u => u.username !== username));
    };

    const onUserOnline = ({ username: userOnline }) => {
      if (userOnline !== username) {
        setOnlineUsers(prev => {
          if (prev.some(u => u.username === userOnline)) return prev;
          return [...prev, { username: userOnline, isOnline: true }];
        });
        addNotification({
          type: "user_online",
          message: `${userOnline} is now online`,
          from: userOnline,
          timestamp: new Date(),
        });
      }
    };

    const onUserOffline = ({ username: userOffline }) => {
      setOnlineUsers(prev => prev.filter(u => u.username !== userOffline));
      addNotification({
        type: "user_offline",
        message: `${userOffline} went offline`,
        from: userOffline,
        timestamp: new Date(),
      });
    };

    const onConversationRequest = ({ from }) => {
      if (from !== username) {
        setConversationRequests(prev => {
          if (prev.some(req => req.from === from)) return prev;
          return [...prev, { from, timestamp: Date.now() }];
        });
        addNotification({
          type: "conversation_request",
          message: `${from} wants to chat with you!`,
          from: from,
          timestamp: new Date(),
        });
      }
    };

    const onNotification = (notification) => {
      addNotification(notification);
    };

    socket.on("message", onMessage);
    socket.on("typing:start", onTypingStart);
    socket.on("typing:stop", onTypingStop);
    socket.on("file", onMessage);
    socket.on("online-users", onOnlineUsers);
    socket.on("user-online", onUserOnline);
    socket.on("user-offline", onUserOffline);
    socket.on("conversation-request", onConversationRequest);
    socket.on("notification", onNotification);

    return () => {
      socket.off("message", onMessage);
      socket.off("typing:start", onTypingStart);
      socket.off("typing:stop", onTypingStop);
      socket.off("file", onMessage);
      socket.off("online-users", onOnlineUsers);
      socket.off("user-online", onUserOnline);
      socket.off("user-offline", onUserOffline);
      socket.off("conversation-request", onConversationRequest);
      socket.off("notification", onNotification);
    };
  }, [username, isDM, dmUsername, pushMessage, addNotification]);

  useEffect(() => {
    const onConnect = () => {
      if (username) socket.emit("join", { username });
    };
    socket.on("connect", onConnect);
    return () => socket.off("connect", onConnect);
  }, [username]);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
    
    // Clear unread messages for current conversation
    if (isDM) {
      const conversationKey = getConversationKey(username, dmUsername);
      setUnreadMessages(prev => ({
        ...prev,
        [conversationKey]: 0
      }));
    } else {
      setUnreadMessages(prev => ({
        ...prev,
        global: 0
      }));
    }
  }, [getCurrentMessages, isDM, username, dmUsername, getConversationKey]);

  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      for (const [key] of pendingMessagesRef.current) {
        const timestamp = parseInt(key.split('-')[0]);
        if (now - timestamp > 10000) {
          pendingMessagesRef.current.delete(key);
        }
      }
    }, 5000);
    
    return () => clearInterval(interval);
  }, []);

  const joinChat = () => {
    const next = safeTrim(nameInput);
    if (!next) return;
    setUsername(next);
    sessionStorage.setItem("loggedUserName", next);
    socket.emit("join", { username: next });
  };

  const startConversation = (targetUser) => {
    if (!username || targetUser === username) return;
    
    setSelectedUserForDM({ username: targetUser });
    setConversationRequests(prev => prev.filter(req => req.from !== targetUser));
    
    // Add to active conversations if not already there
    if (!activeConversations.includes(targetUser)) {
      setActiveConversations(prev => [...prev, targetUser]);
    }
    
    // Clear unread messages for this conversation
    const conversationKey = getConversationKey(username, targetUser);
    setUnreadMessages(prev => ({
      ...prev,
      [conversationKey]: 0
    }));
    
    // Notify server about conversation start
    socket.emit("start-conversation", { withUser: targetUser });
    
    // Close sidebar on mobile after selection
    setShowSidebar(false);
  };

  const switchConversation = (targetUser) => {
    if (targetUser === "global") {
      setSelectedUserForDM(null);
      navigate("/chat", { state: {} });
    } else {
      startConversation(targetUser);
    }
  };

  const acceptConversationRequest = (fromUser) => {
    startConversation(fromUser);
  };

  const sendMessage = () => {
    const text = safeTrim(messageInput);
    if (!text || !username) return;

    if (isTyping) {
      socket.emit("typing:stop", { username, senderId: socket.id, to: dmUsername });
      setIsTyping(false);
    }

    const clientMessageId = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    
    const message = {
      type: "text",
      text,
      timestamp: new Date(),
      username,
      senderId: socket.id,
      clientMessageId,
      ...(isDM ? { to: dmUsername } : {}),
    };

    pendingMessagesRef.current.set(clientMessageId, true);

    socket.emit("message", message);
    
    // Optimistic UI update
    const conversationKey = isDM ? getConversationKey(username, dmUsername) : "global";
    setMessages(prev => ({
      ...prev,
      [conversationKey]: [...(prev[conversationKey] || []), message]
    }));
    
    setMessageInput("");
  };

  const sendFile = (file) => {
    if (!file || !username) return;

    const MAX_FILE_BYTES = 2 * 1024 * 1024;
    if (file.size > MAX_FILE_BYTES) {
      alert("File too large (max 2MB).");
      return;
    }

    setSendingFile(true);
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      const clientMessageId = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      
      const payload = {
        type: "file",
        username,
        timestamp: new Date(),
        senderId: socket.id,
        clientMessageId,
        ...(isDM ? { to: dmUsername } : {}),
        file: {
          name: file.name,
          mime: file.type || "application/octet-stream",
          size: file.size,
          dataUrl,
        },
      };

      pendingMessagesRef.current.set(clientMessageId, true);

      socket.emit("file", payload);
      
      const conversationKey = isDM ? getConversationKey(username, dmUsername) : "global";
      setMessages(prev => ({
        ...prev,
        [conversationKey]: [...(prev[conversationKey] || []), payload]
      }));
      
      setSendingFile(false);
    };
    reader.onerror = () => {
      setSendingFile(false);
      alert("Failed to read file.");
    };
    reader.readAsDataURL(file);
  };

  useEffect(() => {
    if (!username) return;

    if (!safeTrim(messageInput)) {
      if (isTyping) {
        socket.emit("typing:stop", { username, senderId: socket.id, to: dmUsername });
        setIsTyping(false);
      }
      return;
    }

    if (!isTyping) {
      socket.emit("typing:start", { username, senderId: socket.id, to: dmUsername });
      setIsTyping(true);
    }

    const t = setTimeout(() => {
      socket.emit("typing:stop", { username, senderId: socket.id, to: dmUsername });
      setIsTyping(false);
    }, 900);

    return () => clearTimeout(t);
  }, [messageInput, username, isTyping, dmUsername]);

  if (!username) {
    return (
      <div className="min-h-full bg-slate-50">
        <TopNavBar />
        <main className="mx-auto max-w-md px-4 py-10">
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="text-xl font-semibold mb-4">Join chat</div>
            <div className="flex">
              <input
                type="text"
                className="w-full px-3 py-2 border rounded-l-md outline-none"
                placeholder="Enter your name..."
                value={nameInput}
                onChange={(e) => setNameInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") joinChat();
                }}
              />
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded-r-md hover:bg-blue-700"
                onClick={joinChat}
                type="button"
              >
                Start
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-slate-50">
      <TopNavBar />
      
      {/* Mobile Menu Button */}
      <button
        onClick={() => setShowSidebar(!showSidebar)}
        className="lg:hidden fixed bottom-20 right-4 z-50 bg-blue-600 text-white rounded-full p-3 shadow-lg hover:bg-blue-700 transition-all"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>

      {/* Notification Bell */}
      <div className="fixed top-20 right-4 z-50">
        <div className="relative">
          <button
            onClick={() => setShowNotifications(!showNotifications)}
            className="bg-white rounded-full p-2 shadow-lg hover:shadow-xl transition-shadow"
          >
            🔔
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                {unreadCount}
              </span>
            )}
          </button>
          
          {showNotifications && (
            <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-xl border max-h-96 overflow-y-auto z-50">
              <div className="p-3 border-b font-semibold">Notifications</div>
              {notifications.length === 0 ? (
                <div className="p-4 text-center text-gray-500">No notifications</div>
              ) : (
                notifications.map(notif => (
                  <div key={notif.id} className="p-3 border-b hover:bg-gray-50 cursor-pointer" onClick={() => {
                    if (notif.type === "conversation_request") {
                      acceptConversationRequest(notif.from);
                      setShowNotifications(false);
                    } else if (notif.type === "message" && notif.from) {
                      startConversation(notif.from);
                      setShowNotifications(false);
                    }
                  }}>
                    <div className="text-sm">{notif.message}</div>
                    <div className="text-xs text-gray-500 mt-1">
                      {new Date(notif.timestamp).toLocaleTimeString()}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
      
      <main className="mx-auto max-w-6xl px-4 py-6">
        <div className="flex gap-4 relative">
          {/* Conversations Sidebar - Mobile Responsive */}
          <div className={`
            fixed lg:relative lg:block z-40
            ${showSidebar ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
            transition-transform duration-300 ease-in-out
            w-80 lg:w-64 bg-white rounded-2xl border border-slate-200 shadow-sm 
            h-[calc(100vh-100px)] overflow-y-auto
            top-24 left-4 lg:top-auto lg:left-auto
          `}>
            {/* Close button for mobile */}
            <div className="lg:hidden p-3 border-b flex justify-between items-center">
              <span className="font-semibold">Chats</span>
              <button onClick={() => setShowSidebar(false)} className="text-gray-500">
                ✕
              </button>
            </div>

            {/* Global Chat Option */}
            <div 
              className={`p-3 border-b cursor-pointer hover:bg-slate-50 ${!isDM ? 'bg-blue-50' : ''}`}
              onClick={() => switchConversation("global")}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-semibold">🌍 Global Chat</div>
                  <div className="text-xs text-gray-500">Everyone</div>
                </div>
                {unreadMessages.global > 0 && (
                  <span className="bg-red-500 text-white text-xs rounded-full px-2 py-1">
                    {unreadMessages.global}
                  </span>
                )}
              </div>
            </div>
            
            {/* Active Conversations */}
            {activeConversations.length > 0 && (
              <div className="p-3 border-b">
                <div className="text-xs text-gray-500 mb-2">PRIVATE CHATS</div>
                {activeConversations.map(user => (
                  <div 
                    key={user}
                    className={`p-2 rounded-lg cursor-pointer hover:bg-slate-50 mb-1 ${isDM && dmUsername === user ? 'bg-blue-50' : ''}`}
                    onClick={() => switchConversation(user)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium">💬 {user}</div>
                        <div className="text-xs text-green-500">
                          {onlineUsers.some(u => u.username === user) ? '● Online' : '○ Offline'}
                        </div>
                      </div>
                      {unreadMessages[getConversationKey(username, user)] > 0 && (
                        <span className="bg-red-500 text-white text-xs rounded-full px-2 py-1">
                          {unreadMessages[getConversationKey(username, user)]}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* Online Users List */}
            <div className="p-3">
              <div className="text-xs text-gray-500 mb-2">ONLINE USERS ({onlineUsers.length})</div>
              {onlineUsers.map(user => (
                <div key={user.username} className="p-2 hover:bg-slate-50 rounded-lg cursor-pointer" onClick={() => startConversation(user.username)}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">{user.username}</div>
                      <div className="text-xs text-green-500">● Online</div>
                    </div>
                    <button className="text-blue-600 text-sm">
                      {activeConversations.includes(user.username) ? 'Chat' : 'Start'}
                    </button>
                  </div>
                </div>
              ))}
              {onlineUsers.length === 0 && (
                <div className="text-center text-gray-500 text-sm p-4">No other users online</div>
              )}
            </div>
          </div>

          {/* Overlay for mobile sidebar */}
          {showSidebar && (
            <div 
              className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
              onClick={() => setShowSidebar(false)}
            />
          )}
          
          {/* Main Chat Area */}
          <div className="flex-1 rounded-2xl border border-slate-200 bg-white shadow-sm flex flex-col h-[calc(100vh-100px)]">
            <div className="flex items-center justify-between border-b border-slate-200 p-4">
              <div className="flex-1">
                <h1 className="text-lg sm:text-xl font-bold text-slate-900">
                  {isDM ? `Chat with ${dmUsername}` : "Global Chat"}
                </h1>
                <p className="text-xs text-slate-500">
                  Signed in as <span className="font-semibold">{username}</span>
                </p>
              </div>
              <div className="lg:hidden">
                <button
                  onClick={() => setShowSidebar(true)}
                  className="text-blue-600 text-sm font-medium"
                >
                  Chats
                </button>
              </div>
            </div>

            {/* Conversation Requests */}
            {!isDM && conversationRequests.length > 0 && (
              <div className="bg-yellow-50 border-b border-yellow-200 p-3">
                <div className="text-sm font-semibold mb-2">Conversation Requests</div>
                {conversationRequests.map(req => (
                  <div key={req.from} className="flex justify-between items-center bg-white p-2 rounded mb-2 flex-wrap gap-2">
                    <span className="text-sm">{req.from} wants to chat with you</span>
                    <button
                      onClick={() => acceptConversationRequest(req.from)}
                      className="bg-blue-600 text-white px-3 py-1 rounded text-sm"
                    >
                      Accept
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div
              ref={listRef}
              className="flex-1 overflow-y-auto bg-slate-50 p-4"
              style={{ height: "calc(100vh - 280px)" }}
            >
              {getCurrentMessages().map((msg, index) => (
                <div
                  key={`${index}-${msg?.clientMessageId || msg?.timestamp || Math.random()}`}
                  className={`flex flex-col mb-3 ${msg?.senderId && msg.senderId === socket.id
                    ? "items-end"
                    : "items-start"
                    }`}
                >
                  <div
                    className={`p-3 rounded-2xl max-w-[85%] sm:max-w-[70%] break-words shadow-sm ${msg?.senderId && msg.senderId === socket.id
                      ? "bg-blue-600 text-white"
                      : "bg-slate-900 text-white"
                      }`}
                  >
                    <div className="text-[11px] opacity-80 mb-1">
                      {msg?.senderId && msg.senderId === socket.id
                        ? "You"
                        : msg?.username || "Anonymous"}
                    </div>

                    {msg?.type === "file" && msg?.file ? (
                      <div className="space-y-2">
                        <div className="text-sm opacity-90 break-all">{msg.file.name}</div>
                        {typeof msg.file.mime === "string" &&
                          msg.file.mime.startsWith("image/") ? (
                          <a
                            href={msg.file.dataUrl}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <img
                              src={msg.file.dataUrl}
                              alt={msg.file.name}
                              className="max-h-40 rounded border border-white/20"
                            />
                          </a>
                        ) : (
                          <a
                            className="underline text-sm"
                            href={msg.file.dataUrl}
                            download={msg.file.name}
                          >
                            Download
                          </a>
                        )}
                      </div>
                    ) : (
                      <div className="text-sm sm:text-base break-words">
                        {msg?.text}
                      </div>
                    )}
                  </div>
                  <span className="text-slate-500 text-xs mt-1">
                    {msg?.timestamp ? new Date(msg.timestamp).toLocaleTimeString() : ""}
                  </span>
                </div>
              ))}
              {getCurrentMessages().length === 0 && (
                <div className="text-center text-gray-500 mt-20">
                  No messages yet. Start the conversation!
                </div>
              )}
            </div>

            <div className="h-5 px-4 text-xs text-slate-600">
              {typingUsers.length === 1
                ? `${typingUsers[0]} is typing...`
                : typingUsers.length > 1
                  ? `${typingUsers.join(", ")} are typing...`
                  : ""}
            </div>

            <div className="border-t border-slate-200 p-3 sm:p-4">
              {showEmojis ? (
                <div className="mb-3 flex flex-wrap gap-2">
                  {["😀", "😂", "😍", "🥳", "😎", "😭", "😡", "👍", "🙏", "❤️", "🔥", "🎉"].map(
                    (e) => (
                      <button
                        key={e}
                        className="px-2 py-1 bg-slate-100 rounded hover:bg-slate-200 text-lg"
                        onClick={() => {
                          setMessageInput((prev) => prev + e);
                          setShowEmojis(false);
                        }}
                        type="button"
                      >
                        {e}
                      </button>
                    )
                  )}
                </div>
              ) : null}

              <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
                <div className="flex gap-2">
                  <button
                    type="button"
                    className="px-3 py-2 bg-slate-100 rounded hover:bg-slate-200"
                    onClick={() => setShowEmojis((v) => !v)}
                    title="Emojis"
                  >
                    🙂
                  </button>

                  <label
                    className={`px-3 py-2 bg-slate-100 rounded hover:bg-slate-200 cursor-pointer text-center ${sendingFile ? "opacity-60 pointer-events-none" : ""
                      }`}
                    title="Send file (max 2MB)"
                  >
                    📎
                    <input
                      type="file"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) sendFile(f);
                        e.target.value = "";
                      }}
                    />
                  </label>
                </div>

                <input
                  type="text"
                  className="flex-1 px-3 py-2 border rounded-lg outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base"
                  placeholder={isDM ? `Message ${dmUsername}...` : "Message everyone..."}
                  value={messageInput}
                  onChange={(e) => setMessageInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") sendMessage();
                  }}
                />

                <button
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  onClick={sendMessage}
                  type="button"
                >
                  Send
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
